import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from "../../ApiConfig/ApiConfig";
import { FaChevronLeft, FaChevronRight, FaSearch } from 'react-icons/fa';
import Cookies from 'js-cookie';

const AgreementDetails = () => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage] = useState(10);
  
  const navigate = useNavigate();
  const token = Cookies.get('admin');

  useEffect(() => {
    const fetchAgreements = async () => {
      const apiUrl = `${API_BASE_URL}/agreement/fetchagreement`;
      
      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
    
        const data = await response.json();
        console.log('Fetched agreements:', data);
        
        if (data.data && Array.isArray(data.data)) {
          setAgreements(data.data);
        } else {
          throw new Error('Invalid data format received');
        }
      } catch (error) {
        console.error('Error fetching agreements:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAgreements();
  }, [token]);

  // Filter agreements based on search term
  const filteredAgreements = agreements.filter(agreement => {
    if (!agreement) return false;
    
    const searchLower = searchTerm.toLowerCase();
    const customerId = agreement.user?.customerId || '';
    const partnerName = agreement.user?.name || '';
    const location = agreement.agreement?.location || '';
    
    return (
      customerId.toLowerCase().includes(searchLower) ||
      partnerName.toLowerCase().includes(searchLower) ||
      location.toLowerCase().includes(searchLower)
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredAgreements.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAgreements.slice(indexOfFirstItem, indexOfLastItem);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewDetails = (item) => {
    navigate('/admin/dashboard/agreement-details-print', {
      state: { 
        agreement: {
          ...item.agreement,
          ...item.user,
          customerId: item.user?.customerId
        } 
      }
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <span className="text-lg">Loading...</span>
    </div>
  );
  
  if (error) return <div className="text-red-500 p-4">Error: {error}</div>;

  return (
    <div className="p-6 min-h-screen">
      {/* Header with Search Input */}
      <div className="flex justify-between items-center mb-8 flex-col sm:flex-row">
        <h1 className="text-3xl font-bold text-gray-800 mb-4 sm:mb-0">
          Agreement Details
        </h1>
    
        {/* Search Input */}
        <div className="flex-shrink-0 w-full sm:w-1/3 relative">
          <input
            type="text"
            placeholder="Search by Customer ID, Name or Location"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="p-2 pl-10 pr-3 rounded-lg w-full border border-gray-300 shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300"
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>
    
      {/* Agreement Data Cards */}
      <div className="container mx-auto">
        {currentItems.length > 0 ? (
          <>
            <div className="flex flex-wrap justify-between mb-4">
              <p className="font-semibold pl-4 text-[#87909A] w-full sm:w-auto sm:block hidden">Customer ID</p>
              <p className="font-semibold  text-[#87909A] text-left w-full sm:w-auto sm:block hidden">Customer Name</p>
              <p className="font-semibold text-left text-[#87909A] w-full sm:w-auto sm:block hidden">Action</p>
            </div>
            
            {currentItems.map((item, index) => (
              <div
                key={index}
                className={`bg-white shadow-md rounded-lg p-4 mb-4 transition-all duration-300 ease-in-out transform ${
                  index % 2 === 0 ? "bg-gray-50" : "bg-white"
                } group hover:bg-gray-100 hover:scale-[1.02] hover:shadow-lg hover:cursor-pointer`}
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                  {/* Circle with the first letter of the customer's name */}
                  <div className="flex items-center space-x-3">
                    <div className="w-7 h-7 bg-[#1C50A3] text-white rounded-full flex items-center justify-center font-bold group-hover:bg-blue-600">
                      {item.user?.name?.charAt(0).toUpperCase() || 'N'}
                    </div>
                    <p className="text-[#292D32] font-semibold group-hover:text-blue-600">
                      {item.user?.customerId || 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-[#292D32] text-center font-semibold group-hover:text-blue-600">
                      {item.user?.name || 'N/A'}
                    </p>
                  
                  </div>
                  
                  {/* Action button aligned to the right */}
                  <div className="ml-auto text-right">
                    <button
                      onClick={() => handleViewDetails(item)}
                      className="text-[#1C50A3] bg-[#E8F1FF] rounded-lg px-4 py-1 flex items-center space-x-2 transition duration-300"
                    >
                      <span className="font-semibold">View</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="bg-white shadow-md rounded-lg p-8 text-center">
            <p className="text-gray-500">No agreements found</p>
          </div>
        )}
      </div>
    
      {/* Pagination Controls */}
      {filteredAgreements.length > 0 && (
        <div className="flex justify-center items-center mt-6 space-x-3 flex-wrap">
          {/* Previous Button */}
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="px-4 py-2 mx-1 rounded-md disabled:opacity-50 text-gray-600 text-sm font-semibold flex items-center"
          >
            <FaChevronLeft className="mr-2" /> Previous
          </button>
      
          {/* Page Buttons */}
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => handlePageChange(i + 1)}
              className={`px-3 py-1 mx-1 rounded-md text-sm font-semibold ${
                currentPage === i + 1
                  ? "bg-[#1C50A3] text-white"
              : "bg-gray-200 text-gray-600"
              }`}
            >
              {i + 1}
            </button>
          ))}
      
          {/* Next Button */}
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-4 py-2 mx-1 rounded-md disabled:opacity-50 text-gray-600 text-sm font-semibold flex items-center"
          >
            Next <FaChevronRight className="ml-2" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AgreementDetails;